/**
 * Layer Store - Zustand store for document overlay layer management
 *
 * Manages per-document layer toggles (issues, compare diffs, proposed changes).
 * Each document can have different layers available based on completed workflows.
 *
 * @example
 * // Toggle a layer
 * const toggleLayer = useLayerStore((state) => state.toggleLayer)
 * toggleLayer('doc-123', 'issues')
 *
 * // Check if layer is active
 * const isActive = useLayerStore((state) =>
 *   layerSelectors.isLayerActive(state, 'doc-123', 'issues')
 * )
 */

import { create } from 'zustand'
import { persist, devtools } from 'zustand/middleware'

// Layer types - represents different overlay annotations
export type LayerType = 'issues' | 'compare' | 'proposedChanges' | 'negotiation' | 'trackedChanges' | 'comments'

// Per-document layer toggle states
export interface LayerStates {
  issues: boolean
  compare: boolean
  proposedChanges: boolean
  negotiation: boolean
  trackedChanges: boolean
  comments: boolean
}

// Default layer states (all off)
const DEFAULT_LAYER_STATES: LayerStates = {
  issues: false,
  compare: false,
  proposedChanges: false,
  negotiation: false,
  trackedChanges: false,
  comments: false,
}

// Stable empty array to avoid new reference on every selector call
const EMPTY_LAYERS: LayerType[] = []

export interface LayerStore {
  // State
  activeLayers: Record<string, LayerStates> // documentId -> layer on/off states
  availableLayers: Record<string, LayerType[]> // documentId -> available layer types
  // Task 239.1 / Task 242: Maps documentId -> { layerType: analysisRunId }.
  // Each layer type can have its own source analysis run ID (e.g., issues from review workflow,
  // compare from compare workflow). Keyed by documentId, then by layer type.
  layerSourceAnalysisRunIds: Record<string, Record<string, string>> // documentId -> { layerType: analysisRunId }
  // Plan 167: marker for "user explicitly chose this layer's source run".
  // Set by sidebar clicks and by completion handlers (which treat
  // user-initiated runs as explicit). Detection probes / passive callers do
  // not set the marker AND must skip source updates when the marker exists
  // (anti-stomp guard inside addAvailableLayer).
  explicitLayerSelections: Record<string, Record<string, true>>

  // Actions
  toggleLayer: (documentId: string, layerType: LayerType) => void
  setLayerActive: (documentId: string, layerType: LayerType, active: boolean) => void
  // Plan 167: options.explicit=true means "user-initiated, override any prior
  // explicit selection". Without the flag, calls are treated as passive and
  // will NOT overwrite a source ID for which an explicit marker exists.
  addAvailableLayer: (documentId: string, layerType: LayerType, sourceAnalysisRunId?: string, options?: { explicit?: boolean }) => void
  removeAvailableLayer: (documentId: string, layerType: LayerType) => void
  clearDocumentLayers: (documentId: string) => void
  resetAllLayers: () => void
  // Issue #771 follow-up: nuke the persisted source ID + explicit marker for
  // ONE layer type on ONE document. Used by layer-detection probes when they
  // discover the persisted source workflow no longer exists (deleted /
  // never-existed-on-this-machine after localStorage hydration from a stale
  // session). After calling this, a subsequent passive addAvailableLayer
  // probe writes the fresh source unimpeded by the anti-stomp guard.
  clearLayerSource: (documentId: string, layerType: LayerType) => void
}

export const useLayerStore = create<LayerStore>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        activeLayers: {},
        availableLayers: {},
        layerSourceAnalysisRunIds: {},
        explicitLayerSelections: {},

        // Actions
        toggleLayer: (documentId, layerType) => set(
          (state) => {
            const currentStates = state.activeLayers[documentId] || DEFAULT_LAYER_STATES
            return {
              activeLayers: {
                ...state.activeLayers,
                [documentId]: {
                  ...currentStates,
                  [layerType]: !currentStates[layerType],
                },
              },
            }
          },
          false,
          'toggleLayer'
        ),

        setLayerActive: (documentId, layerType, active) => set(
          (state) => {
            const currentStates = state.activeLayers[documentId] || DEFAULT_LAYER_STATES
            return {
              activeLayers: {
                ...state.activeLayers,
                [documentId]: {
                  ...currentStates,
                  [layerType]: active,
                },
              },
            }
          },
          false,
          'setLayerActive'
        ),

        addAvailableLayer: (documentId, layerType, sourceAnalysisRunId, options) => set(
          (state) => {
            const explicit = options?.explicit === true
            const currentAvailable = state.availableLayers[documentId] || []
            const currentStates = state.activeLayers[documentId] || DEFAULT_LAYER_STATES
            const alreadyAvailable = currentAvailable.includes(layerType)
            const existingMarker = state.explicitLayerSelections[documentId]?.[layerType] === true

            // Plan 167 four-row anti-stomp table:
            // - explicit:true + runId       → always set source, set/keep marker (user signal wins).
            // - !explicit + runId, no marker → set source (auto-default).
            // - !explicit + runId, marker   → SKIP source update (anti-stomp).
            // - no runId                    → add availability only; do not touch source/marker.

            const updates: Partial<LayerStore> = {}

            // (a) Add to availableLayers + activate if first add — applies to any call.
            if (!alreadyAvailable) {
              updates.availableLayers = {
                ...state.availableLayers,
                [documentId]: [...currentAvailable, layerType],
              }
              updates.activeLayers = {
                ...state.activeLayers,
                [documentId]: {
                  ...currentStates,
                  [layerType]: true, // Auto-activate on first add (legacy behaviour preserved)
                },
              }
            }

            // (b) Source ID write rules.
            if (sourceAnalysisRunId) {
              const canWriteSource = explicit || !existingMarker
              if (canWriteSource) {
                const existingDoc = state.layerSourceAnalysisRunIds[documentId] || {}
                const existingId = (existingDoc as Record<string, string>)[layerType]
                if (existingId !== sourceAnalysisRunId) {
                  updates.layerSourceAnalysisRunIds = {
                    ...state.layerSourceAnalysisRunIds,
                    [documentId]: {
                      ...existingDoc,
                      [layerType]: sourceAnalysisRunId,
                    },
                  }
                }
              }
              // else: passive caller + existing marker → silently skip (anti-stomp).
            }

            // (c) Explicit marker write — only when caller declared it explicit.
            if (explicit) {
              const existingMarkerDoc = state.explicitLayerSelections[documentId] || {}
              if (existingMarkerDoc[layerType] !== true) {
                updates.explicitLayerSelections = {
                  ...state.explicitLayerSelections,
                  [documentId]: {
                    ...existingMarkerDoc,
                    [layerType]: true,
                  },
                }
              }
            }

            return Object.keys(updates).length > 0 ? updates : state
          },
          false,
          'addAvailableLayer'
        ),

        removeAvailableLayer: (documentId, layerType) => set(
          (state) => {
            const currentAvailable = state.availableLayers[documentId] || []
            const currentStates = state.activeLayers[documentId] || DEFAULT_LAYER_STATES

            return {
              availableLayers: {
                ...state.availableLayers,
                [documentId]: currentAvailable.filter((t) => t !== layerType),
              },
              activeLayers: {
                ...state.activeLayers,
                [documentId]: {
                  ...currentStates,
                  [layerType]: false, // Deactivate when removing
                },
              },
            }
          },
          false,
          'removeAvailableLayer'
        ),

        clearDocumentLayers: (documentId) => set(
          (state) => {
            const { [documentId]: _, ...restActive } = state.activeLayers
            const { [documentId]: __, ...restAvailable } = state.availableLayers
            const { [documentId]: ___, ...restSourceIds } = state.layerSourceAnalysisRunIds
            const { [documentId]: ____, ...restMarkers } = state.explicitLayerSelections

            return {
              activeLayers: restActive,
              availableLayers: restAvailable,
              layerSourceAnalysisRunIds: restSourceIds,
              explicitLayerSelections: restMarkers,
            }
          },
          false,
          'clearDocumentLayers'
        ),

        resetAllLayers: () => set(
          {
            activeLayers: {},
            availableLayers: {},
            layerSourceAnalysisRunIds: {},
            explicitLayerSelections: {},
          },
          false,
          'resetAllLayers'
        ),

        clearLayerSource: (documentId, layerType) => set(
          (state) => {
            const existingDoc = state.layerSourceAnalysisRunIds[documentId]
            const existingMarkerDoc = state.explicitLayerSelections[documentId]
            const hadSource = existingDoc && (existingDoc as Record<string, string>)[layerType] !== undefined
            const hadMarker = existingMarkerDoc && existingMarkerDoc[layerType] === true
            if (!hadSource && !hadMarker) return state

            const updates: Partial<LayerStore> = {}
            if (hadSource) {
              const { [layerType]: _removedSource, ...restSourceForDoc } = existingDoc as Record<string, string>
              updates.layerSourceAnalysisRunIds = {
                ...state.layerSourceAnalysisRunIds,
                [documentId]: restSourceForDoc,
              }
            }
            if (hadMarker) {
              const { [layerType]: _removedMarker, ...restMarkerForDoc } = existingMarkerDoc as Record<string, true>
              updates.explicitLayerSelections = {
                ...state.explicitLayerSelections,
                [documentId]: restMarkerForDoc,
              }
            }
            return updates
          },
          false,
          'clearLayerSource'
        ),
      }),
      {
        name: 'taylaw-layer-state',
        version: 1,
        partialize: (state) => ({
          activeLayers: state.activeLayers,
          availableLayers: state.availableLayers,
          layerSourceAnalysisRunIds: state.layerSourceAnalysisRunIds,
          explicitLayerSelections: state.explicitLayerSelections,
        }),
        migrate: (persistedState, version) => layerStorePersistMigrate(persistedState, version),
      }
    ),
    { name: 'LayerStore' }
  )
)

/**
 * Plan 167 (Task 1.4 section E): persist migration.
 *
 * Pre-rev source IDs were set by the old first-match-iteration-order
 * detection. Under the new latest-by-`created_at` default, the next probe
 * would overwrite them anyway, so we wipe them on migrate and let the next
 * probe (or click) populate cleanly. Also default `explicitLayerSelections`
 * to `{}` for stores that pre-date the marker.
 *
 * Exported for unit testing.
 */
export function layerStorePersistMigrate(persistedState: any, version: number): any {
  if (persistedState === null || persistedState === undefined) return persistedState
  if (typeof persistedState !== 'object') return persistedState
  if (version < 1) {
    return {
      ...persistedState,
      layerSourceAnalysisRunIds: {},
      explicitLayerSelections: {},
    }
  }
  return persistedState
}

// Selector helpers for common patterns
export const layerSelectors = {
  getActiveLayers: (state: LayerStore, documentId: string): LayerStates => {
    return state.activeLayers[documentId] || DEFAULT_LAYER_STATES
  },

  getAvailableLayers: (state: LayerStore, documentId: string): LayerType[] => {
    return state.availableLayers[documentId] || EMPTY_LAYERS
  },

  isLayerActive: (state: LayerStore, documentId: string, layerType: LayerType): boolean => {
    const layers = state.activeLayers[documentId] || DEFAULT_LAYER_STATES
    return layers[layerType]
  },

  hasAnyLayers: (state: LayerStore, documentId: string): boolean => {
    const available = state.availableLayers[documentId] || EMPTY_LAYERS
    return available.length > 0
  },

  // Task 239.1 / Task 242: Get the workflow analysis run ID for a specific layer type on a document.
  // Defaults to 'issues' for backward compatibility.
  getSourceAnalysisRunId: (state: LayerStore, documentId: string, layerType: string = 'issues'): string | null => {
    const docIds = state.layerSourceAnalysisRunIds[documentId]
    if (!docIds) return null
    // Support new nested structure
    if (typeof docIds === 'object' && !Array.isArray(docIds)) {
      return (docIds as Record<string, string>)[layerType] || null
    }
    // Legacy fallback: if somehow a string was stored directly (shouldn't happen after migration)
    return null
  },
}
